<template>
  <div class="flex flex-col flex-1">
    <ZccVerticalAccordion>
      <template v-slot:title>
        <div class="flex flex-row justify-between flex-1">
          <h2 class="text-xl font-bold m-0 p-1">
            {{ title }}
          </h2>
          <div class="flex flex-row gap-6">
            <ZccInput
              class="flex flex-row gap-4 items-center content-center"
              :label="'Filter'"
              title="Verberg oude en inactieve diensten"
              :model-value="hideOldSchedules"
              @update:model-value="(value) => emit('update:modelValue', value)"
              :type="'checkbox'"
              id="checkbox"
            ></ZccInput>
          </div>
        </div>
      </template>

      <template v-slot:content>
        <div class="flex flex-col gap-4 flex-1 mt-4 mx-10">
          <template v-if="!loading">
            <template v-if="hasSchedules">
              <!-- cluster group names (headers)-->
              <div class="flex flex-row gap-4 flex-1">
                <div class="w-10"></div>
                <div
                  v-for="clusterGroupName in clusterGroupNames"
                  :key="`clusterGroupName-${String(clusterGroupName)}`"
                  class="flex-1 p-2 flex flex-row gap-2"
                >
                  <font-awesome-icon
                    :icon="['fas', 'location-dot']"
                    class="py-1"
                  />
                  <span class="font-bold text-lg">{{ clusterGroupName }}</span>
                </div>
              </div>

              <template
                v-for="scheduleGroup in sortedScheduleGroups"
                v-bind:key="`scheduleGroup-${scheduleGroup.Name}`"
              >
                <ZccHorizontalAccordion
                  class="flex flex-row gap-4"
                  v-if="scheduleGroup.Clusters && scheduleGroup.Clusters.length > 0"
                  :scheduleGroupName="scheduleGroup.Name"
                >
                  <template v-slot:content>
                    <template
                      v-for="cluster in scheduleGroup.Clusters || []"
                      v-bind:key="`cluster-${scheduleGroup.Name}-${cluster.Id}`"
                    >
                      <div
                        class="flex flex-col flex-1 min-w-[300px]"
                        v-if="scheduleGroup.Name.startsWith('HCA')"
                      >
                        <!-- information inside clusters -->
                        <div
                          class="flex flex-col gap-2"
                          v-if="scheduleGroup.Name.endsWith('24')"
                        >
                          <!-- filter cluster west based on name -->
                          <ZccSchedule
                            v-for="schedule in sortSchedulesOn24Hours(cluster)"
                            v-bind:key="`schedule-${cluster.Id}-${schedule.Id}`"
                            :schedule="schedule"
                            :activeScheduleId="activeScheduleId"
                            :setActiveScheduleId="setActiveScheduleId"
                          ></ZccSchedule>
                          <ZccAddSchedule
                            :date="date"
                            :cluster="cluster.DisplayName"
                            :schedule-group="scheduleGroup.Name"
                          ></ZccAddSchedule>
                        </div>
                        <div class="flex flex-col gap-2" v-else>
                          <ZccSchedule
                            v-for="schedule in sortSchedulesOnStartTime(cluster.Schedules)"
                            v-bind:key="`schedule-${cluster.Id}-${schedule.Id}`"
                            :schedule="schedule"
                            :activeScheduleId="activeScheduleId"
                            :setActiveScheduleId="setActiveScheduleId"
                          ></ZccSchedule>
                          <ZccAddSchedule
                            :date="date"
                            :cluster="cluster.DisplayName"
                            :schedule-group="scheduleGroup.Name"
                          ></ZccAddSchedule>
                        </div>
                      </div>

                      <!-- LZV: één kolom per district cluster -->
                      <div
                        class="flex flex-col flex-1 min-w-[300px] gap-2"
                        v-if="scheduleGroup.Name === 'LZV'"
                      >
                        <ZccSchedule
                          v-for="schedule in sortSchedulesOnStartTime(cluster.Schedules)"
                          v-bind:key="`schedule-${cluster.Id}-${schedule.Id}`"
                          :schedule="schedule"
                          :activeScheduleId="activeScheduleId"
                          :setActiveScheduleId="setActiveScheduleId"
                        ></ZccSchedule>
                        <ZccAddSchedule
                          :date="date"
                          :cluster="cluster.DisplayName"
                          :schedule-group="scheduleGroup.Name"
                        ></ZccAddSchedule>
                      </div>

                      <!-- information outside clusters -->
                      <template v-if="!scheduleGroup.Name.startsWith('HCA') && scheduleGroup.Name !== 'LZV'">
                        <div
                          v-for="index in 4"
                          :key="`index-${cluster.Id}-${index}`"
                          class="flex flex-col flex-1 min-w-[300px] empty:hidden gap-2"
                        >
                          <template
                            v-for="(
                              schedule, indexSchedule
                            ) in sortSchedulesOnStartTime(
                              cluster.Schedules,
                              true
                            )"
                            v-bind:key="`schedule-${cluster.Id}-${schedule.Id}`"
                          >
                            <div
                              class="flex flex-col gap-2 empty:hidden"
                              v-if="
                                schedule.Id !== undefined &&
                                (index == indexSchedule + 1 ||
                                  (indexSchedule >= 4 &&
                                    indexSchedule - 3 == index) ||
                                  (indexSchedule >= 8 &&
                                    indexSchedule - 7 == index))
                              "
                            >
                              <ZccSchedule
                                :schedule="schedule"
                                :activeScheduleId="activeScheduleId"
                                :setActiveScheduleId="setActiveScheduleId"
                              ></ZccSchedule>
                            </div>
                            <div
                              class="flex flex-col"
                              v-if="
                                (cluster.Schedules.length ==
                                  indexSchedule + 1 &&
                                  index == indexSchedule + 2) ||
                                (cluster.Schedules.length == indexSchedule &&
                                  cluster.Schedules.length >= 4 &&
                                  index == indexSchedule - 3) ||
                                (cluster.Schedules.length == indexSchedule &&
                                  cluster.Schedules.length >= 8 &&
                                  index == indexSchedule - 7)
                              "
                            >
                              <ZccAddSchedule
                                :date="date"
                                :cluster="cluster.DisplayName"
                                :schedule-group="scheduleGroup.Name"
                              ></ZccAddSchedule>
                            </div>
                            <div
                              v-else
                              class="flex flex-col empty:hidden"
                            ></div>
                          </template>
                        </div>
                      </template>
                    </template>
                  </template>
                </ZccHorizontalAccordion>
              </template>
            </template>
            <template v-else>
              <p>Geen roosters gevonden</p>
            </template>
          </template>
          <template v-else>
            <div>Aan het laden...</div>
          </template>
        </div>
      </template>
    </ZccVerticalAccordion>
  </div>
</template>

<script lang="ts" setup>
import { Schedule, ScheduleGroup, Cluster } from '@planbord/shared/models';
import {
  ZccHorizontalAccordion,
  ZccSchedule,
  ZccVerticalAccordion,
  ZccInput,
  ZccAddSchedule,
} from '@planbord/ui/components';
import { ref, computed, watch } from 'vue';
import { useSignalR } from '@dreamonkey/vue-signalr/dist/composable';

const activeScheduleId = ref<number | null>(null);
function setActiveScheduleId(id: number | null) {
  activeScheduleId.value = id;
}

const props = defineProps<{
  title: string;
  date: Date;
  data: ScheduleGroup[];
  modelValue: unknown;
  loading: boolean;
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: unknown): void }>();
const hideOldSchedules = ref(props.modelValue);

// Use a reactive ref for scheduleGroups, update only when props.data changes
const scheduleGroups = ref<ScheduleGroup[]>([]);
watch(
  () => props.data,
  (newData) => {
    scheduleGroups.value = JSON.parse(JSON.stringify(newData));
  },
  { immediate: true }
);

// --- Computed properties for all expensive operations ---
const hasSchedules = computed(() =>
  scheduleGroups.value.some(
    (x: ScheduleGroup) => Array.isArray(x.Clusters) && x.Clusters.length > 0
  )
);

const clusterGroupNames = computed(() => {
  return scheduleGroups.value?.[0]?.Clusters?.map(
    (x: { DisplayName: unknown }) => x.DisplayName
  );
});

const sortedScheduleGroups = computed(() => {
  const groups = scheduleGroups.value;
  const scheduleGroupsExceptLCAMCA = groups.filter(
    (x) => !x.Name.startsWith('LCA') && !x.Name.startsWith('MCA') && x.Name !== 'LZV'
  );
  const idx = scheduleGroupsExceptLCAMCA.findIndex((x) => x.Name.startsWith('HCA DD'));
  const scheduleGroupsUntilHCADD = scheduleGroupsExceptLCAMCA.slice(0, idx + 2);
  const scheduleGroupsFromHCADD = scheduleGroupsExceptLCAMCA.slice(idx + 2);
  const scheduleGroupsLCAMCA = groups.filter(
    (x) => x.Name.startsWith('LCA') || x.Name.startsWith('MCA') || x.Name === 'LZV'
  );
  return [
    ...scheduleGroupsUntilHCADD,
    ...scheduleGroupsLCAMCA,
    ...scheduleGroupsFromHCADD,
  ];
});

function sortSchedulesOnStartTime(schedules: Schedule[], addEmpty = false) {
  if (schedules && schedules.length > 1) {
    const sortedSchedules = [...schedules];
    if (addEmpty) {
      sortedSchedules.push({} as Schedule);
    }
    return sortedSchedules.sort((x, y) => {
      return new Date(x.StartTime).getTime() - new Date(y.StartTime).getTime();
    });
  }
  return schedules;
}

function sortSchedulesOn24Hours(cluster: Cluster) {
  if (cluster.Schedules && cluster.Schedules.length > 1) {
    switch (cluster.Uname) {
      case 'Cluster West':
        return [...cluster.Schedules].sort((x, y) => x.Shift.Uname.localeCompare(y.Shift.Uname));
      case 'Cluster Zuid':
        return [...cluster.Schedules].sort((x, y) => y.Shift.Uname.localeCompare(x.Shift.Uname));
      case 'Cluster Oost':
        return [...cluster.Schedules].sort((x, y) => {
          return x.Shift.Uname == 'AO'
            ? -1
            : x.Shift.Uname == 'AB'
            ? y.Shift.Uname == 'AO' ? 1 : -1
            : y.Shift.Uname == 'AO' || y.Shift.Uname == 'AB' ? 1 : -1;
        });
      default:
        break;
    }
  }
  return cluster.Schedules;
}

// --- SignalR event handling ---
const signalr = useSignalR();
signalr.on('addSchedule', (...messages) => {
  messages.forEach((schedule: Schedule) => {
    if (
      schedule.Id &&
      new Date(schedule.DateTime).getDate() == props.date.getDate()
    ) {
      let groupName = '';
      if (schedule.Shift.ShiftGroup.Uname.startsWith('Cluster')) {
        const startTime = new Date(schedule.StartTime);
        const endTime = new Date(schedule.EndTime);
        const middleTime = new Date(
          startTime.getTime() + (endTime.getTime() - startTime.getTime()) / 2
        ).getHours();
        if (startTime.getHours() == endTime.getHours()) groupName = 'HCA 24';
        else if (middleTime >= 8 && middleTime <= 17) groupName = 'HCA DD';
        else if (middleTime >= 17 && middleTime <= 23) groupName = 'HCA AD';
        else if (middleTime >= 23 || middleTime <= 8) groupName = 'HCA ND';
      } else {
        if (schedule.Shift.ShiftGroup.DisplayName.startsWith('LCA')) groupName = 'LCA';
        else if (schedule.Shift.ShiftGroup.DisplayName.startsWith('MCA')) groupName = 'MCA';
        else if (schedule.Shift.ShiftGroup.Uname.startsWith('LZV')) groupName = 'LZV';
        else if (schedule.Shift.ShiftGroup.DisplayName.startsWith('VTA')) groupName = 'VTA';
        else if (schedule.Shift.ShiftGroup.DisplayName.startsWith('OVD')) groupName = 'OVDG';
      }
      if (groupName) {
        // Replace the group in a reactive way
        const groupIdx = scheduleGroups.value.findIndex((x) => x.Name === groupName);
        if (groupIdx !== -1) {
          const group = scheduleGroups.value[groupIdx];
          if (group.Clusters) {
            const clusterIdx = group.Clusters.findIndex(
              (x: Cluster) => x.DisplayName == schedule.Shift.ShiftGroup.DisplayName
            );
            if (clusterIdx !== -1) {
              if (!group.Clusters[clusterIdx].Schedules) {
                group.Clusters[clusterIdx].Schedules = [schedule];
              } else {
                group.Clusters[clusterIdx].Schedules = [
                  ...group.Clusters[clusterIdx].Schedules || [],
                  schedule,
                ];
              }
              // Replace the group to trigger reactivity
              scheduleGroups.value = [
                ...scheduleGroups.value.slice(0, groupIdx),
                { ...group },
                ...scheduleGroups.value.slice(groupIdx + 1),
              ];
            }
          }
        }
      }
    }
  });
});
</script>
